import React from "react";
import { COST_CATEGORIES, formatPLN } from "../lib/api";
import { Plus, X } from "lucide-react";
import { Button } from "./ui/button";

// Helper: emit "" instead of 0 in input so users don't fight a leading zero
const toDisplay = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "";
  return String(v);
};

const newCustomItem = () => ({
  id: Math.random().toString(36).slice(2, 10),
  name: "",
  amount: 0,
});

export default function CostTable({
  costs,
  customItems = [],
  onChange,
  onChangeCustomItems,
  readOnly = false,
  title,
  accent = "var(--primary)",
  showStandard = true,
}) {
  const total =
    (showStandard
      ? COST_CATEGORIES.reduce((s, c) => s + Number(costs?.[c.key] || 0), 0)
      : 0) +
    customItems.reduce((s, it) => s + Number(it?.amount || 0), 0);

  const handle = (key, raw) => {
    const v = raw === "" ? 0 : Number(raw);
    if (Number.isNaN(v)) return;
    onChange({ ...costs, [key]: v });
  };

  const updateItem = (idx, patch) => {
    const next = customItems.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChangeCustomItems?.(next);
  };

  const addItem = () => {
    onChangeCustomItems?.([...(customItems || []), newCustomItem()]);
  };

  const removeItem = (idx) => {
    onChangeCustomItems?.(customItems.filter((_, i) => i !== idx));
  };

  return (
    <div className="surface overflow-hidden" data-testid="cost-table">
      {title && (
        <div
          className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2"
          style={{ borderLeft: `3px solid ${accent}` }}
        >
          <div className="heading text-sm font-semibold uppercase tracking-wider">
            {title}
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)] bg-[var(--surface-2)]/40">
        <div className="col-span-7">{showStandard ? "Kategoria" : "Pozycja"}</div>
        <div className="col-span-5 text-right">Kwota (PLN)</div>
      </div>
      {showStandard && COST_CATEGORIES.map((c, idx) => (
        <div
          key={c.key}
          className={`grid grid-cols-12 items-center px-5 ${
            idx !== COST_CATEGORIES.length - 1 || customItems.length > 0
              ? "border-b border-[var(--border)]"
              : ""
          }`}
          data-testid={`cost-row-${c.key}`}
        >
          <div className="col-span-7 py-2 text-sm font-medium">{c.label}</div>
          <div className="col-span-5">
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              disabled={readOnly}
              value={toDisplay(costs?.[c.key])}
              placeholder="0"
              onChange={(e) => handle(c.key, e.target.value)}
              className="cost-input"
              data-testid={`cost-input-${c.key}`}
            />
          </div>
        </div>
      ))}

      {customItems.map((it, idx) => (
        <div
          key={it.id ?? idx}
          className="grid grid-cols-12 items-center px-5 py-2 border-b border-[var(--border)] gap-2"
          data-testid={`custom-row-${idx}`}
        >
          <div className="col-span-7 flex items-center gap-2">
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-[var(--text-muted)] hover:text-[var(--danger)] transition-colors shrink-0"
                data-testid={`custom-remove-${idx}`}
                aria-label="Usuń pozycję"
              >
                <X size={14} />
              </button>
            )}
            <input
              type="text"
              disabled={readOnly}
              value={it.name || ""}
              placeholder="np. LED, oświetlenie, akcesoria..."
              onChange={(e) => updateItem(idx, { name: e.target.value })}
              className="cost-input text-left"
              data-testid={`custom-name-${idx}`}
            />
          </div>
          <div className="col-span-5">
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              disabled={readOnly}
              value={toDisplay(it.amount)}
              placeholder="0"
              onChange={(e) =>
                updateItem(idx, {
                  amount: e.target.value === "" ? 0 : Number(e.target.value),
                })
              }
              className="cost-input"
              data-testid={`custom-amount-${idx}`}
            />
          </div>
        </div>
      ))}

      {!readOnly && (
        <div className="px-5 py-2 border-b border-[var(--border)]">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addItem}
            data-testid="add-custom-item-btn"
            className="text-[var(--primary)] hover:bg-[var(--surface-2)]"
          >
            <Plus size={14} className="mr-1" />
            {showStandard ? "Dodaj pozycję (np. LED)" : "Dodaj koszt"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-12 items-center px-5 py-3 bg-[var(--surface-2)]/60">
        <div className="col-span-7 text-sm font-semibold">Suma</div>
        <div
          className="col-span-5 text-right text-base font-semibold tabular-nums"
          data-testid="cost-total"
        >
          {formatPLN(total)}
        </div>
      </div>
    </div>
  );
}
